import { domAnimation, LazyMotion, m } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import { LogoIcon } from "../logo-icon";

const listVariant = {
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
  hidden: {
    opacity: 0,
  },
};

const itemVariant = {
  hidden: { opacity: 0 },
  show: { opacity: 1 },
};

type LinkItem = {
  path: string;
  title: string;
};

const links: LinkItem[] = [
  { title: "Features", path: "/#job-seekers" },
  { title: "Roadmap", path: "/#roadmap" },
  { title: "What users say", path: "/#testimonials" },
];

export function LandingHeader({
  showSectionLinks = true,
}: {
  showSectionLinks?: boolean;
}) {
  const [isOpen, setOpen] = useState(false);
  const sectionLinks = showSectionLinks ? links : [];

  const handleToggleMenu = () => {
    setOpen((prev) => {
      document.body.style.overflow = prev ? "" : "hidden";
      return !prev;
    });
  };

  return (
    <LazyMotion features={domAnimation} strict>
      <header className="fixed top-0 right-0 left-0 z-50 w-full bg-background/50 backdrop-blur-md">
        <nav
          aria-label="Primary navigation"
          className="relative mx-auto flex w-full items-center justify-between px-4 py-3 xl:gap-6 xl:px-6 xl:py-4 2xl:px-8"
        >
          <Link className="flex items-center" href="/">
            <LogoIcon />
          </Link>

          <div className="hidden items-center gap-6 lg:flex">
            <ul className="flex items-center gap-6">
              {sectionLinks.map(({ path, title }) => (
                <li key={path}>
                  <a
                    className="cursor-pointer font-medium text-[15px] text-muted-foreground transition-colors hover:text-foreground"
                    href={path}
                  >
                    {title}
                  </a>
                </li>
              ))}
            </ul>

            <div className="border-border border-l pl-4">
              <Link
                className="font-medium text-[15px] text-primary transition-colors hover:text-primary/80"
                href="/login"
              >
                Sign in
              </Link>
            </div>
          </div>

          <button
            className="relative z-50 p-2 lg:hidden"
            onClick={handleToggleMenu}
            type="button"
          >
            <svg
              aria-label="menu toggle"
              fill="none"
              height={13}
              role="img"
              width={18}
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 12.195v-2.007h18v2.007H0Zm0-5.017V5.172h18v2.006H0Zm0-5.016V.155h18v2.007H0Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </nav>

        {isOpen && (
          <m.div
            animate={{ opacity: 1 }}
            className="-top-[2px] pointer-events-auto fixed right-0 bottom-0 left-0 z-50 h-screen w-full bg-background px-2"
            initial={{ opacity: 0 }}
          >
            <div className="relative mt-4 ml-px flex justify-between p-3 px-4">
              <button className="z-50" onClick={handleToggleMenu} type="button">
                <LogoIcon />
              </button>

              <button
                className="absolute top-2 right-[10px] z-50 ml-auto p-2 lg:hidden"
                onClick={handleToggleMenu}
                type="button"
              >
                <svg
                  aria-label="menu-toggle"
                  className="fill-primary"
                  height={24}
                  role="img"
                  width={24}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 0h24v24H0V0z" fill="none" />
                  <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
                </svg>
              </button>
            </div>

            <div className="h-screen overflow-auto pb-[150px]">
              <m.ul
                animate="show"
                className="mb-8 space-y-8 overflow-auto px-3 pt-8 text-muted-foreground text-xl"
                initial="hidden"
                variants={listVariant}
              >
                {sectionLinks.map(({ path, title }) => (
                  <m.li key={path} variants={itemVariant}>
                    <a
                      className="cursor-pointer"
                      href={path}
                      onClick={handleToggleMenu}
                    >
                      {title}
                    </a>
                  </m.li>
                ))}

                <m.li
                  className="mt-auto space-y-4 border-t pt-8"
                  variants={itemVariant}
                >
                  <div className="flex items-center justify-between">
                    <Link
                      className="font-normal text-primary text-xl"
                      href="/login"
                      onClick={handleToggleMenu}
                    >
                      Sign in
                    </Link>
                  </div>
                </m.li>
              </m.ul>
            </div>
          </m.div>
        )}
      </header>
    </LazyMotion>
  );
}
